import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { MutationFunction } from '@apollo/client'
import { setAlertQueue } from 'cache/alerts'
import { useRefreshApprovalFlowSubscription } from 'hooks/use-refresh-approval-flow-subscription'
import {
  SaveDecisionEditPageDetailsResponse,
  SaveDecisionEditPageDetailsVariables,
} from 'queries/mutations/save-decision-edit-page-details'
import { MessageType } from 'ts/enums/messageType.enums'

// Timeout fallback: show a retry alert if the approval refresh subscription does not arrive within 5 minutes.
const APPROVAL_REFRESH_WAIT_TIMEOUT_MS = 300_000

interface UseApprovalRefreshFlowOptions {
  dealId: number | undefined
  dealNumber: string
  decisionType: string
  decisionReason: string
  emailId: string
  userId: string | undefined
  userEmail: string | undefined
  saveDecisionEditPageDetails: MutationFunction<
    SaveDecisionEditPageDetailsResponse,
    SaveDecisionEditPageDetailsVariables
  >
  onSaveSuccess: () => void
}

interface UseApprovalRefreshFlowResult {
  isAwaitingApprovalRefresh: boolean
  executeSaveAndAwaitRefresh: () => Promise<void>
}

/**
 * Encapsulates the full approval-refresh subscription flow for the Decision Edit Page:
 * - On successful save, starts waiting for the `onRefreshApprovalFlow` subscription event.
 * - On subscription data: redirects to the Decision Page.
 * - On subscription error: shows a retry alert, allowing the user to retry the save.
 * - On timeout: shows a retry alert as fallback if subscription does not arrive within 5 minutes.
 * - Timer cleanup is explicit; timers never stack across retries.
 */
export const useApprovalRefreshFlow = ({
  dealId,
  dealNumber,
  decisionType,
  decisionReason,
  emailId,
  userId,
  userEmail,
  saveDecisionEditPageDetails,
  onSaveSuccess,
}: UseApprovalRefreshFlowOptions): UseApprovalRefreshFlowResult => {
  const history = useHistory()

  const [isAwaitingApprovalRefresh, setIsAwaitingApprovalRefresh] = useState(false)

  // Refs kept current inside subscription callbacks to avoid stale closures.
  const awaitingRedirectRef = useRef(false)
  const pendingRedirectPathRef = useRef<string | null>(null)
  const approvalRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const showTimeoutRetryAlertRef = useRef<(redirectPath: string | null) => void>(() => undefined)

  const clearApprovalRefreshTimer = useCallback(() => {
    if (approvalRefreshTimerRef.current) {
      clearTimeout(approvalRefreshTimerRef.current)
      approvalRefreshTimerRef.current = null
    }
  }, [])

  const stopAwaitingApprovalRefresh = useCallback(() => {
    awaitingRedirectRef.current = false
    pendingRedirectPathRef.current = null
    clearApprovalRefreshTimer()
    setIsAwaitingApprovalRefresh(false)
  }, [clearApprovalRefreshTimer])

  const redirectToPendingWorkflow = useCallback(() => {
    const redirectPath = pendingRedirectPathRef.current
    stopAwaitingApprovalRefresh()
    if (redirectPath) {
      history.push(redirectPath)
    }
  }, [history, stopAwaitingApprovalRefresh])

  const redirectToWorkflowPath = useCallback(
    (redirectPath: string | null) => {
      stopAwaitingApprovalRefresh()
      if (redirectPath) {
        history.push(redirectPath)
      }
    },
    [history, stopAwaitingApprovalRefresh]
  )

  const executeSaveAndAwaitRefresh = useCallback(
    async (isRetry = false): Promise<void> => {
      const parsedDealNumber = Number(dealNumber)

      if (!dealId || !dealNumber || !Number.isFinite(parsedDealNumber) || !userId || !userEmail) {
        setAlertQueue({
          type: MessageType.ERROR,
          message: 'Missing required information to save changes.',
        })
        return
      }

      try {
        const result = await saveDecisionEditPageDetails({
          variables: {
            input: {
              dealId,
              dealNumber: parsedDealNumber,
              userId,
              userEmailId: userEmail,
              // CPQ-39825: Backend contract: `isRetry` must be idempotent. The initial save already
              // succeeded; when `true`, the server must not re-apply changes and should only
              // re-drive/re-emit the approval refresh notification for the saved state.
              isRetry,
            },
          },
        })

        if (result?.data?.saveDecisionEditPageDetails?.isSuccess) {
          setAlertQueue({
            type: MessageType.SUCCESS,
            message:
              result.data.saveDecisionEditPageDetails.message ?? 'Changes saved successfully.',
          })
          onSaveSuccess()
          clearApprovalRefreshTimer()
          const redirectPath = `/workflow/${dealNumber}/${decisionType}/${decisionReason}/${emailId}`
          pendingRedirectPathRef.current = redirectPath
          awaitingRedirectRef.current = true
          setIsAwaitingApprovalRefresh(true)
        } else {
          setAlertQueue({
            type: MessageType.ERROR,
            message:
              result?.data?.saveDecisionEditPageDetails?.errorMessage ??
              'Failed to save changes. Please try again.',
          })
        }
      } catch (error) {
        setAlertQueue({
          type: MessageType.ERROR,
          message: 'An error occurred while saving changes. Please try again.',
        })
        console.error('Error saving decision edit page details:', error)
      }
    },
    [
      clearApprovalRefreshTimer,
      dealId,
      dealNumber,
      userId,
      userEmail,
      saveDecisionEditPageDetails,
      decisionType,
      decisionReason,
      emailId,
      onSaveSuccess,
    ]
  )

  const showSubscriptionErrorRetryAlert = useCallback(
    (redirectPath: string | null) => {
      setAlertQueue({
        type: MessageType.WARNING,
        message:
          'Failed to receive approval refresh response. Please click Retry to save and apply your changes again.',
        okButtonLabel: 'Retry',
        onOk: () => {
          executeSaveAndAwaitRefresh(true)
        },
        onClose: () => {
          redirectToWorkflowPath(redirectPath)
        },
      })
    },
    [executeSaveAndAwaitRefresh, redirectToWorkflowPath]
  )

  const showTimeoutRetryAlert = useCallback(
    (redirectPath: string | null) => {
      setAlertQueue({
        type: MessageType.WARNING,
        message:
          'Approval refresh is taking longer than expected. Please click Retry to save and apply your changes.',
        okButtonLabel: 'Retry',
        onOk: () => {
          executeSaveAndAwaitRefresh(true)
        },
        onClose: () => {
          redirectToWorkflowPath(redirectPath)
        },
      })
    },
    [executeSaveAndAwaitRefresh, redirectToWorkflowPath]
  )

  useEffect(() => {
    showTimeoutRetryAlertRef.current = showTimeoutRetryAlert
  }, [showTimeoutRetryAlert])

  // Subscription runs in background from page load.
  // onData  → save succeeded & subscription responded → redirect.
  // onError → subscription failed → show retry alert allowing user to try again.
  useRefreshApprovalFlowSubscription(dealId, {
    onData: useCallback(() => {
      if (awaitingRedirectRef.current && pendingRedirectPathRef.current) {
        redirectToPendingWorkflow()
      }
    }, [redirectToPendingWorkflow]),
    onError: useCallback(() => {
      if (awaitingRedirectRef.current) {
        const redirectPath = pendingRedirectPathRef.current
        stopAwaitingApprovalRefresh()
        showSubscriptionErrorRetryAlert(redirectPath)
      }
    }, [showSubscriptionErrorRetryAlert, stopAwaitingApprovalRefresh]),
  })

  // Timeout fallback: if subscription does not arrive within 5 minutes, show retry alert.
  useEffect(() => {
    if (!isAwaitingApprovalRefresh) {
      clearApprovalRefreshTimer()
      return
    }

    clearApprovalRefreshTimer()
    approvalRefreshTimerRef.current = setTimeout(() => {
      if (awaitingRedirectRef.current) {
        const redirectPath = pendingRedirectPathRef.current
        stopAwaitingApprovalRefresh()
        showTimeoutRetryAlertRef.current(redirectPath)
      }
    }, APPROVAL_REFRESH_WAIT_TIMEOUT_MS)

    return () => {
      clearApprovalRefreshTimer()
    }
  }, [clearApprovalRefreshTimer, isAwaitingApprovalRefresh, stopAwaitingApprovalRefresh])

  return { isAwaitingApprovalRefresh, executeSaveAndAwaitRefresh }
}
